<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import TextInput from '../TextInput/TextInput.vue';
  import { toggleButtonClass } from './PasswordInput.css';
  import type {
    PasswordInputEmits,
    PasswordInputProps,
  } from './PasswordInput.types';

  withDefaults(defineProps<PasswordInputProps>(), {
    modelValue: null,
    disabled: false,
    required: false,
    autofocus: false,
  });

  const emit = defineEmits<PasswordInputEmits>();

  const { t } = useLocale('ds-components');

  const visible = ref(false);

  const inputType = computed(() => (visible.value ? 'text' : 'password'));

  const toggleIcon = computed(() =>
    visible.value ? TUNDR_ICONS.EYE_OPEN : TUNDR_ICONS.EYE_CLOSE,
  );

  const toggleAriaLabel = computed(() =>
    visible.value
      ? t('password_input.hide_aria_label')
      : t('password_input.show_aria_label'),
  );

  const toggleVisibility = () => {
    visible.value = !visible.value;
  };

  const onUpdate = (value: string) => {
    emit('update:modelValue', value);
  };

  const onBlur = (event: FocusEvent) => {
    emit('blur', event);
  };
</script>

<template>
  <TextInput
    :id="id"
    :test-id="testId"
    :label="label"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :model-value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :disabled-reason="disabledReason"
    :required="required"
    :autofocus="autofocus"
    :name="name"
    :size="size"
    :type="inputType"
    :input-mode="inputMode"
    :pattern="pattern"
    :autocomplete="autocomplete"
    :aria-label="ariaLabel"
    @update:model-value="onUpdate"
    @blur="onBlur"
  >
    <template v-if="$slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>
    <template #icon>
      <button
        type="button"
        :class="toggleButtonClass"
        :disabled="disabled"
        :aria-label="toggleAriaLabel"
        :aria-pressed="visible"
        @click="toggleVisibility"
      >
        <FontIcon :icon-name="toggleIcon" />
      </button>
    </template>
  </TextInput>
</template>
